import React from "react";
import { Link } from "react-router-dom";
import daydreamLogo from "./ddf_logo.png";
import addupLogo from "./addup_logo.png";
import kanaLogo from "./kana_mascot_white_inside.png";
import lunaLogo from "./lunatarot_logo.png";
import addupShotKo1 from "./screenshots/addup-ko-1.png";
import addupShotKo2 from "./screenshots/addup-ko-2.png";
import addupShotEn1 from "./screenshots/addup-en-1.png";
import addupShotEn2 from "./screenshots/addup-en-2.png";
import kanaShot1 from "./screenshots/kanatomo-1.png";
import kanaShot2 from "./screenshots/kanatomo-2.png";
import lunaShot1 from "./screenshots/lunatarot-1.png";
import lunaShot2 from "./screenshots/lunatarot-2.png";
import stirredLogo from "./stirred_logo.png";
import stirredShotKo1 from "./screenshots/stirred-ko-1.png";
import stirredShotKo2 from "./screenshots/stirred-ko-2.png";
import stirredShotEn1 from "./screenshots/stirred-en-1.png";
import stirredShotEn2 from "./screenshots/stirred-en-2.png";
import stirredShotJa1 from "./screenshots/stirred-ja-1.png";
import stirredShotJa2 from "./screenshots/stirred-ja-2.png";
import sueoLogo from "./dailysign_logo.png";
import sueoShot1 from "./screenshots/dailysign-1.png";
import sueoShot2 from "./screenshots/dailysign-2.png";
import { trackStoreClick } from "./firebaseAnalytics";
import "./App.css";

type Locale = "KO" | "EN" | "JA" | "ES" | "FR" | "PT" | "VI";

const LOCALE_STORAGE_KEY = "ddf-home-locale";
const locales: Locale[] = ["KO", "EN", "JA", "ES", "FR", "PT", "VI"];

const localeNames: Record<Locale, string> = {
  KO: "한국어",
  EN: "English",
  JA: "日本語",
  ES: "Español",
  FR: "Français",
  PT: "Português",
  VI: "Tiếng Việt",
};

const detectLocale = (): Locale => {
  if (typeof window === "undefined") {
    return "KO";
  }

  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && locales.includes(stored as Locale)) {
    return stored as Locale;
  }

  const browserLocales = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language];

  for (const locale of browserLocales) {
    const normalized = locale.toLowerCase();
    if (normalized.startsWith("ko")) return "KO";
    if (normalized.startsWith("ja")) return "JA";
    if (normalized.startsWith("es")) return "ES";
    if (normalized.startsWith("fr")) return "FR";
    if (normalized.startsWith("pt")) return "PT";
    if (normalized.startsWith("vi")) return "VI";
    if (normalized.startsWith("en")) return "EN";
  }

  return "EN";
};

type AppCopy = {
  title: string;
  subtitle: string;
  desc: string;
  features: Array<[string, string]>;
};

type Copy = {
  nav: { about: string; numbers: string; contact: string };
  eyebrow: string;
  heroTitle: [string, string];
  heroBody: string;
  heroStats: [string, string, string];
  heroCtas: [string, string];
  heroTiles: { addup: string; kana: string; luna: string; stirred: string; sueo: string; newBadge: string; shippedBadge: string; soonBadge: string };
  marquee: string[];
  common: {
    featuredApp: string;
    viewDetails: string;
    readOnline: string;
    appStore: string;
    googlePlay: string;
    currentLocale: string;
    moreComing: string;
    studioNotes: string;
    byTheNumbers: string;
    roadmap: string;
    global: string;
    contact: string;
    promises: string;
  };
  addup: AppCopy & { sub: string };
  kana: AppCopy & { sub: string };
  luna: AppCopy & { sub: string };
  stirred: AppCopy & { sub: string };
  sueo: AppCopy & { sub: string; name: string };
  web: { note: string; visit: string; cards: Array<{ name: string; tag: string; desc: string; langs: string; url: string; accent: string }> };
  principles: Array<{ number: string; label: string; title: string; body: string; accent: string }>;
  stats: Array<[string, string, string, string]>;
  roadmap: Array<[string, string, string, string, string]>;
  global: { title: [string, string]; desc: string };
  contact: { title: [string, string]; cards: Array<{ label: string; value: string; cta: string; accent: string }> };
  footer: string;
};

const baseCopy: Record<Locale, Copy> = {
  KO: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody:
      "Daydream Factory는 서울에서 조용하고 오래 남는 앱을 만드는 1인 스튜디오입니다. 생산성, 언어 학습, 감정 기록, 취미까지 — 일상을 돕는 작은 앱들을 iOS와 Android로 만들고 있습니다.",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["앱 둘러보기", "스튜디오 이야기"],
    heroTiles: {
      addup: "할 일 · 루틴 · 목표 로드맵",
      kana: "예문으로 익히는 일본어",
      luna: "감정 기록 타로 저널",
      stirred: "만들며 익히는 칵테일 가이드",
      sueo: "하루 5단어, 한국수어 학습",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["큰 목표도, 한 번에 한 단계씩", "매일 10분의 일본어", "오리지널 78장 타로 덱", "직접 만들며 익히는 칵테일 70잔", "하루 5단어, 손으로 말하는 한국수어", "9개 언어 동시 출시", "1인 스튜디오, 천천히", "기록은 기기 안에만"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "자세히 보기",
      readOnline: "웹에서 읽기",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "현재 선택",
      moreComing: "More coming",
      studioNotes: "우리가 일하는 방식",
      byTheNumbers: "작은 스튜디오, 또렷한 숫자",
      roadmap: "작업해 온, 작업할 것들",
      global: "One studio, many languages",
      contact: "함께하세요",
      promises: "세 가지 약속.",
    },
    addup: {
      title: "Add Up,",
      subtitle: "큰 목표도, 한 번에 한 단계씩.",
      desc: "3초 만에 할 일을 적고, 큰 목표는 단계별 로드맵으로 쪼개서 하나씩 잠금 해제하는 할 일·루틴 앱입니다. 체크할 때마다 작은 축하가 함께하고, 주간 타임라인에서 하루를 시간 흐름으로 볼 수 있습니다. 로그인 없이 iCloud로 동기화됩니다.",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["3초 입력", "열면 바로 입력창, 마찰 제로"], ["로드맵", "단계를 잠금 해제하듯 목표 달성"], ["완료 축하", "체크마다 작은 축하, 성장 배지"], ["타임라인", "드래그로 일정을 옮기는 주간 뷰"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "예문으로 익히는 일본어.",
      desc: "JLPT N1부터 N5까지, 공식 등급 단어를 예문과 함께 익히는 일본어 학습 앱입니다. 단순히 외우는 대신 문맥 속에서 단어를 붙잡고, 듣고, 복습하는 흐름을 조용하게 설계했습니다.",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "공식 등급 단어"], ["문맥 학습", "예문 기반 설계"], ["TTS 발음", "원어민 음성"], ["로컬 저장", "기기 안에 남는 학습 기록"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "밤하늘의 작은 리딩 앱.",
      desc: "78장의 오리지널 Mystic Vintage Gold Tarot 덱과 함께, 오늘의 감정과 질문을 조용히 적어 내려가는 타로 저널 앱입니다. 기록은 기기 안에 남고, 더 긴 해석이 필요할 때만 AI 분석을 불러옵니다.",
      sub: "iOS · Android · 7 languages",
      features: [["78장 덱", "Mystic Vintage Gold"], ["다양한 스프레드", "오늘의 카드 · 3장 리딩"], ["AI 해석", "필요할 때만 더 길게"], ["로컬 저장", "기기 안에만 남는 기록"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "읽는 레시피 말고, 만드는 칵테일.",
      desc: "레시피를 찾아보고, 단계별로 직접 만들어보고, 나만의 바를 채워가는 인터랙티브 칵테일 가이드입니다. 70종의 도감을 검색과 필터로 탐색하고, 실전 제조 모드로 한 잔을 완성하세요. 고급 바 무드의 다크 전용 디자인입니다.",
      sub: "iOS · Android · 3 languages",
      features: [["70종 도감", "클래식부터 지역 대표까지"], ["실전 제조", "단계별로 직접 만들기"], ["내 바", "보유 재료로 찾는 오늘의 한 잔"], ["나만의 레시피", "직접 구성하고 저장"]],
    },
    sueo: {
      name: "매일수어",
      title: "매일수어,",
      subtitle: "하루 5단어, 가족과 함께 배우는 수어.",
      desc: "국립국어원 한국수어사전의 실제 수어 영상으로 배우는 한국수어(KSL) 학습 앱입니다. 가족 호칭부터 감정·일상 표현까지 생활 순서로 짠 20단계 커리큘럼을 하루 5단어씩, 따라하기 미러 연습과 함께 익힙니다.",
      sub: "iOS · Android · 한국어",
      features: [["하루 5단어", "부담 없는 데일리 루틴과 스트릭"], ["진짜 수어 영상", "국립국어원 수어사전 300개"], ["따라하기", "카메라 미러로 거울처럼 연습"], ["20단계 커리큘럼", "가족 호칭부터 생활 순서로"]],
    },
    web: {
      note: "설치 없이 브라우저에서 바로",
      visit: "바로가기",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "별자리 운세, 꿈해몽, 78장 타로 카드 사전을 계정 없이 무료로 보는 웹 서비스입니다.", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "보이즈앤걸즈", tag: "3분 심리테스트", desc: "연애·직장·소비·수면까지, 3분이면 끝나는 6문항 심리테스트 모음. 가입도 저장도 없이 결과는 브라우저에서만 계산됩니다.", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "천천히 짓습니다.", body: "한 번에 하나의 앱에 집중합니다. 빠른 출시보다 다듬어진 디테일과 오래 쓰이는 감각을 선택합니다.", accent: "lav" },
      { number: "02", label: "Quietly", title: "조용한 인터페이스.", body: "알림과 광고로 사용자를 몰아붙이지 않습니다. 필요할 때 잠깐 열고, 닫는 도구로 남게 하려 합니다.", accent: "sky" },
      { number: "03", label: "Privately", title: "기록은 기기 안에.", body: "개인 데이터는 가능한 한 사용자 기기에만 남도록 설계하고, 필요한 외부 처리 흐름은 정책 문서에서 분명하게 안내합니다.", accent: "rose" },
    ],
    stats: [["05", "Apps", "출시 4 · 준비 중 1", "blue"], ["09", "Languages", "앱별 최대 9개 언어 동시 출시", "sky"], ["100%", "Local-first", "학습·리딩·할 일 기록은 기기 중심 저장", "rose"], ["1x", "Team size", "1인 스튜디오", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "일본어 학습. 첫 출시작.", "Shipped", "done"], ["2026", "LunaTarot", "78장 오리지널 덱과 함께한 타로 저널.", "Shipped", "done"], ["2026", "Add Up", "할 일·루틴·목표 로드맵. App Store 출시.", "Shipped", "done"], ["2026", "Stirred", "인터랙티브 칵테일 가이드. App Store · Google Play 출시.", "Shipped", "done"], ["2026", "매일수어", "하루 5단어 한국수어 학습. 출시 준비 중.", "Coming soon", "now"], ["Later", "더 작은 것들", "조용하고 다정한 도구들을 계속 짓습니다.", "Soon", "next"]],
    global: { title: ["세계 어디서나,", "같은 톤으로."], desc: "Daydream Factory의 앱들은 한국어, 영어, 일본어를 포함해 최대 9개 언어로 함께 출시됩니다. 각 언어로 번역될 때도 제품의 말투와 감정을 잃지 않도록 다듬고 있습니다." },
    contact: {
      title: ["만들고 싶은", "작은 앱이 있나요?"],
      cards: [
        { label: "Email", value: "support@daydreamfactory.net", cta: "메일 보내기", accent: "sun" },
        { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "스튜디오 소개", accent: "rose" },
        { label: "Collab", value: "앱 협업 / 제안 환영합니다", cta: "협업 문의", accent: "lav" },
      ],
    },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
  EN: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody:
      "Daydream Factory is a one-person studio in Seoul making quiet, lasting apps — small tools for productivity, language learning, journaling, and hobbies on iOS and Android.",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["Explore apps", "Studio notes"],
    heroTiles: {
      addup: "To-dos · routines · goal roadmaps",
      kana: "Japanese through examples",
      luna: "An emotion journal with tarot",
      stirred: "An interactive cocktail guide",
      sueo: "Korean Sign Language, daily",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["Big goals, one unlocked step at a time", "Ten minutes of Japanese a day", "Original 78-card tarot deck", "Seventy cocktails you make yourself", "Korean Sign Language, five words a day", "Released in up to 9 languages", "A one-person studio, slowly", "Your records stay close"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "View details",
      readOnline: "Read on the web",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "Current locale",
      moreComing: "More coming",
      studioNotes: "How we work",
      byTheNumbers: "A small studio, clear numbers",
      roadmap: "What we built, what comes next",
      global: "One studio, many languages",
      contact: "Get in touch",
      promises: "Three promises.",
    },
    addup: {
      title: "Add Up,",
      subtitle: "big goals, one unlocked step at a time.",
      desc: "A to-do and routine app where capturing a task takes three seconds, and big goals become step-by-step roadmaps that unlock one stage at a time. Every check-off comes with a small celebration, and the weekly timeline shows your day as a flow of time. No login — synced with iCloud.",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["3-second capture", "The input bar is right there"], ["Roadmaps", "Unlock goals step by step"], ["Celebrations", "Small cheers and growth badges"], ["Timeline", "Drag cards to move your day"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "Japanese through example sentences.",
      desc: "KanaTomo is a Japanese vocabulary app built around JLPT N1-N5 words, example sentences, audio, and review loops. It is designed to help words stay with you through context, not memorization alone.",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "Official level vocabulary"], ["Context learning", "Example-based design"], ["TTS audio", "Native-style pronunciation"], ["Local records", "Study history stays on-device"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "a small reading app for the night.",
      desc: "Built around the original 78-card Mystic Vintage Gold Tarot deck, LunaTarot is a quiet tarot journal app for writing down feelings and questions. Reading records stay on the device, and AI analysis appears only when needed.",
      sub: "iOS · Android · 7 languages",
      features: [["78-card deck", "Mystic Vintage Gold"], ["Multiple spreads", "Daily card · 3-card spread"], ["AI reading", "Longer interpretation on demand"], ["Local records", "Reading history stays on-device"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "don't just read recipes — make the cocktail.",
      desc: "An interactive cocktail guide for discovering recipes, making them step by step, and building a bar of your own. Browse a 70-cocktail encyclopedia with search and filters, then complete each drink in practice mode — all in a dark, bar-at-night design.",
      sub: "iOS · Android · 3 languages",
      features: [["70 cocktails", "Classics to regional signatures"], ["Practice mode", "Make each drink step by step"], ["My bar", "Drinks from what's on your shelf"], ["My recipes", "Compose and save your own"]],
    },
    sueo: {
      name: "DailySign",
      title: "DailySign,",
      subtitle: "five words of Korean Sign Language a day.",
      desc: "A Korean Sign Language (KSL) learning app built on real sign videos from the National Institute of Korean Language dictionary. A 20-stage curriculum ordered around daily life — from family terms to feelings — learned five words a day with mirror practice. Korean-language app.",
      sub: "iOS · Android · Korean",
      features: [["Daily 5", "A light routine with streaks"], ["Real KSL videos", "300 dictionary sign videos"], ["Mirror practice", "Compare with your camera side by side"], ["20-stage curriculum", "Family terms first, life order next"]],
    },
    web: {
      note: "Right in the browser, no install",
      visit: "Visit",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "A free web service for daily horoscopes, dream interpretation, and a 78-card tarot dictionary — no account needed.", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "Boys & Girls", tag: "3-minute psychology tests", desc: "Quick six-question personality tests about dating, work, spending, and sleep. No sign-up, no storage — results are computed in your browser. Korean-language service.", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "We build slowly.", body: "We focus on one app at a time. We choose refinement and longevity over speed.", accent: "lav" },
      { number: "02", label: "Quietly", title: "Quiet interfaces.", body: "We do not push people with noisy prompts. These are tools you open when you need them.", accent: "sky" },
      { number: "03", label: "Privately", title: "Records stay close.", body: "We keep personal data as close to the device as possible and explain external processing clearly in policy pages.", accent: "rose" },
    ],
    stats: [["05", "Apps", "4 shipped · 1 coming soon", "blue"], ["09", "Languages", "Up to 9 languages per app", "sky"], ["100%", "Local-first", "Study, reading, and to-do records are device-centered", "rose"], ["1x", "Team size", "Solo studio", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "Japanese learning. The first release.", "Shipped", "done"], ["2026", "LunaTarot", "A tarot journal with an original 78-card deck.", "Shipped", "done"], ["2026", "Add Up", "To-dos, routines, and goal roadmaps. Now on the App Store.", "Shipped", "done"], ["2026", "Stirred", "An interactive cocktail guide. Now on the App Store and Google Play.", "Shipped", "done"], ["2026", "DailySign", "Korean Sign Language, five words a day. Coming soon.", "Coming soon", "now"], ["Later", "Smaller things", "More quiet and gentle tools will follow.", "Soon", "next"]],
    global: { title: ["Available worldwide,", "with the same tone."], desc: "Daydream Factory apps ship in up to nine languages including Korean, English, and Japanese. We try to keep the same emotional tone across every translation." },
    contact: {
      title: ["Do you have", "a small app to make?"],
      cards: [
        { label: "Email", value: "support@daydreamfactory.net", cta: "Send an email", accent: "sun" },
        { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "About the studio", accent: "rose" },
        { label: "Collab", value: "Open to app ideas and collaborations", cta: "Start a conversation", accent: "lav" },
      ],
    },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
  JA: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody:
      "Daydream Factory はソウルを拠点とする一人のソフトウェアスタジオです。生産性・語学学習・記録・趣味まで、日常に寄り添う小さなアプリを iOS と Android 向けに丁寧につくっています。",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["アプリを見る", "スタジオについて"],
    heroTiles: {
      addup: "タスク · ルーティン · 目標ロードマップ",
      kana: "例文で身につける日本語",
      luna: "感情を記録するタロットジャーナル",
      stirred: "作って学ぶカクテルガイド",
      sueo: "1日5単語の韓国手話学習",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["大きな目標も、一歩ずつ", "毎日10分の日本語", "オリジナル78枚タロット", "自分で作って覚える70杯", "1日5単語の韓国手話", "最大9言語で同時リリース", "一人のスタジオ、ゆっくり", "記録は身近に残る"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "詳しく見る",
      readOnline: "ウェブで読む",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "現在の言語",
      moreComing: "More coming",
      studioNotes: "私たちの作り方",
      byTheNumbers: "小さなスタジオ、はっきりした数字",
      roadmap: "これまでとこれから",
      global: "One studio, many languages",
      contact: "お問い合わせ",
      promises: "3つの約束。",
    },
    addup: {
      title: "Add Up,",
      subtitle: "大きな目標も、一歩ずつ。",
      desc: "3秒でタスクを書き留め、大きな目標は段階ごとのロードマップに分けて、一つずつロック解除していく ToDo・ルーティンアプリです。チェックのたびに小さなお祝いがあり、週間タイムラインで一日を時間の流れとして見渡せます。ログイン不要、iCloud で同期します。",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["3秒入力", "開いたらすぐ入力バー"], ["ロードマップ", "段階をロック解除するように達成"], ["完了のお祝い", "チェックごとに小さな祝福"], ["タイムライン", "ドラッグで予定を動かす週間ビュー"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "例文で身につける日本語。",
      desc: "KanaTomo は JLPT N1-N5 の単語を、例文・音声・復習の流れの中で学べる日本語学習アプリです。暗記だけでなく、文脈の中で言葉が残るように設計されています。",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "公式レベル語彙"], ["文脈学習", "例文ベース設計"], ["TTS 音声", "自然な発音"], ["ローカル記録", "学習履歴を端末内に保存"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "夜のための小さなリーディングアプリ。",
      desc: "オリジナル 78 枚デッキ Mystic Vintage Gold Tarot とともに、感情や問いを書き留める静かなタロットジャーナルアプリです。記録は端末内に残り、必要なときだけ AI 分析を呼び出せます。",
      sub: "iOS · Android · 7 languages",
      features: [["78枚デッキ", "Mystic Vintage Gold"], ["多様なスプレッド", "今日のカード · 3枚引き"], ["AI 解釈", "必要なときだけ長めに"], ["ローカル記録", "履歴は端末内に保存"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "読むだけのレシピから、つくるカクテルへ。",
      desc: "レシピを探し、手順に沿って実際に作り、自分だけのバーを育てるインタラクティブなカクテルガイドです。70種の図鑑を検索とフィルターで探索し、実践モードで一杯を完成させましょう。夜のバーのようなダーク専用デザインです。",
      sub: "iOS · Android · 3 languages",
      features: [["70種の図鑑", "クラシックからご当地まで"], ["実践モード", "手順に沿って一杯を完成"], ["マイバー", "手元の材料から探す一杯"], ["マイレシピ", "自分で構成して保存"]],
    },
    sueo: {
      name: "DailySign",
      title: "DailySign,",
      subtitle: "1日5単語、韓国手話を毎日。",
      desc: "韓国国立国語院の手話辞典の実際の映像で学ぶ韓国手話（KSL）学習アプリです。家族の呼称から感情・日常表現まで、生活順の20段階カリキュラムを1日5単語ずつ、ミラー練習とともに学びます。※現在は韓国語のみ対応です。",
      sub: "iOS · Android · 韓国語",
      features: [["1日5単語", "無理のない毎日のルーティン"], ["本物の手話映像", "辞典映像300本"], ["ミラー練習", "カメラと並べて鏡のように"], ["20段階カリキュラム", "家族の呼称から生活順に"]],
    },
    web: {
      note: "インストール不要、ブラウザですぐ",
      visit: "開く",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "星占い・夢占い・78枚のタロット辞典をアカウントなしで無料で楽しめるウェブサービスです。", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "Boys & Girls", tag: "3分の心理テスト", desc: "恋愛・仕事・消費・睡眠まで、3分で終わる6問の心理テスト集。登録も保存もなく、結果はブラウザ内でのみ計算されます。※韓国語のみ。", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "ゆっくり作ります。", body: "一度に一つのアプリに集中し、速さよりも完成度と余韻を選びます。", accent: "lav" },
      { number: "02", label: "Quietly", title: "静かなインターフェース。", body: "騒がしい通知で急かしません。必要なときだけ開く道具でありたいと考えています。", accent: "sky" },
      { number: "03", label: "Privately", title: "記録はできるだけ近くに。", body: "個人データはできるだけ端末の近くに置き、外部処理が必要な場合はポリシーで明確に説明します。", accent: "rose" },
    ],
    stats: [["05", "Apps", "リリース 4 · 準備中 1", "blue"], ["09", "Languages", "アプリごとに最大 9 言語", "sky"], ["100%", "Local-first", "学習・記録・タスクは端末中心", "rose"], ["1x", "Team size", "一人のスタジオ", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "日本語学習。最初のリリース。", "Shipped", "done"], ["2026", "LunaTarot", "オリジナル78枚デッキのタロットジャーナル。", "Shipped", "done"], ["2026", "Add Up", "タスク・ルーティン・目標ロードマップ。App Store で公開中。", "Shipped", "done"], ["2026", "Stirred", "インタラクティブなカクテルガイド。App Store・Google Play で公開中。", "Shipped", "done"], ["2026", "DailySign", "1日5単語の韓国手話学習。近日公開。", "Coming soon", "now"], ["Later", "さらに小さなもの", "静かでやさしい道具を作り続けます。", "Soon", "next"]],
    global: { title: ["世界のどこでも,", "同じトーンで。"], desc: "Daydream Factory のアプリは韓国語・英語・日本語を含む最大 9 言語でリリースされます。どの言語でも同じ空気感が伝わるように整えています。" },
    contact: {
      title: ["作ってみたい", "小さなアプリはありますか？"],
      cards: [
        { label: "Email", value: "support@daydreamfactory.net", cta: "メールを送る", accent: "sun" },
        { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "スタジオ紹介", accent: "rose" },
        { label: "Collab", value: "アプリ提案や協業を歓迎します", cta: "相談する", accent: "lav" },
      ],
    },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
  ES: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody: "Daydream Factory es un estudio de software de una sola persona en Seúl. Crea pequeñas apps para la productividad, el aprendizaje de idiomas, los diarios personales y los hobbies, para iOS y Android.",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["Ver apps", "Sobre el estudio"],
    heroTiles: {
      addup: "Tareas · rutinas · hojas de ruta",
      kana: "Japonés a través de ejemplos",
      luna: "Diario emocional con tarot",
      stirred: "Guía interactiva de cócteles",
      sueo: "Lengua de señas coreana, a diario",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["Grandes metas, un paso a la vez", "Diez minutos de japonés al día", "Baraja original de 78 cartas", "Setenta cócteles que preparas tú", "Lengua de señas coreana, cinco palabras al día", "Hasta 9 idiomas por app", "Un estudio de una persona, despacio", "Tus registros permanecen cerca"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "Ver detalles",
      readOnline: "Leer en la web",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "Idioma actual",
      moreComing: "More coming",
      studioNotes: "Cómo trabajamos",
      byTheNumbers: "Un estudio pequeño, números claros",
      roadmap: "Lo hecho y lo que viene",
      global: "One studio, many languages",
      contact: "Contacto",
      promises: "Tres promesas.",
    },
    addup: {
      title: "Add Up,",
      subtitle: "grandes metas, un paso desbloqueado a la vez.",
      desc: "Una app de tareas y rutinas donde anotar algo toma tres segundos, y las grandes metas se dividen en hojas de ruta que se desbloquean etapa por etapa. Cada tarea completada trae una pequeña celebración, y la línea de tiempo semanal muestra tu día como un flujo. Sin registro — sincronizada con iCloud.",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["Captura en 3 segundos", "La barra de entrada siempre lista"], ["Hojas de ruta", "Desbloquea metas paso a paso"], ["Celebraciones", "Pequeños festejos e insignias"], ["Línea de tiempo", "Arrastra tarjetas para mover tu día"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "japonés a través de ejemplos.",
      desc: "KanaTomo es una app de vocabulario japonés basada en palabras JLPT, ejemplos, audio y repaso. Está diseñada para que las palabras permanezcan por contexto, no solo por memorización.",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "Vocabulario oficial"], ["Aprendizaje contextual", "Diseño basado en ejemplos"], ["Audio TTS", "Pronunciación natural"], ["Registro local", "Historial guardado en el dispositivo"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "una pequeña app de lectura nocturna.",
      desc: "Con la baraja original Mystic Vintage Gold Tarot de 78 cartas, LunaTarot es una app silenciosa para registrar preguntas y emociones. El historial queda en el dispositivo y el análisis por IA aparece solo cuando se necesita.",
      sub: "iOS · Android · 7 languages",
      features: [["Baraja de 78 cartas", "Mystic Vintage Gold"], ["Varios spreads", "Carta del día · 3 cartas"], ["Lectura con IA", "Interpretación más larga cuando hace falta"], ["Registro local", "Historial guardado en el dispositivo"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "no solo leas recetas: prepara el cóctel.",
      desc: "Una guía interactiva de cócteles para descubrir recetas, prepararlas paso a paso y construir tu propio bar. Explora una enciclopedia de 70 cócteles con búsqueda y filtros, y completa cada copa en el modo práctica — todo con un ambiente oscuro de bar.",
      sub: "iOS · Android · 3 languages",
      features: [["70 cócteles", "De los clásicos a los regionales"], ["Modo práctica", "Prepara cada copa paso a paso"], ["Mi bar", "Copas con lo que tienes en casa"], ["Mis recetas", "Crea y guarda las tuyas"]],
    },
    sueo: {
      name: "DailySign",
      title: "DailySign,",
      subtitle: "cinco palabras de lengua de señas coreana al día.",
      desc: "Una app para aprender la lengua de señas coreana (KSL) con vídeos reales del diccionario del Instituto Nacional de la Lengua Coreana. Un plan de 20 etapas en orden de vida diaria, cinco palabras al día con práctica en espejo. App en coreano.",
      sub: "iOS · Android · Coreano",
      features: [["5 al día", "Una rutina ligera con rachas"], ["Vídeos reales", "300 vídeos del diccionario"], ["Práctica en espejo", "Compárate con la cámara"], ["20 etapas", "De la familia a la vida diaria"]],
    },
    web: {
      note: "En el navegador, sin instalar",
      visit: "Visitar",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "Un servicio web gratuito de horóscopos, interpretación de sueños y un diccionario de tarot de 78 cartas — sin cuenta.", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "Boys & Girls", tag: "Tests de personalidad de 3 minutos", desc: "Tests de seis preguntas sobre amor, trabajo, gastos y sueño. Sin registro ni almacenamiento: los resultados se calculan en tu navegador. Servicio en coreano.", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "Construimos despacio.", body: "Nos enfocamos en una app a la vez. Elegimos el acabado y la duración por encima de la velocidad.", accent: "lav" },
      { number: "02", label: "Quietly", title: "Interfaces silenciosas.", body: "No empujamos a la gente con ruido. Queremos ser herramientas que se abren solo cuando hacen falta.", accent: "sky" },
      { number: "03", label: "Privately", title: "Los registros se quedan cerca.", body: "Mantenemos los datos personales lo más cerca posible del dispositivo y explicamos claramente cualquier procesamiento externo.", accent: "rose" },
    ],
    stats: [["05", "Apps", "4 publicadas · 1 muy pronto", "blue"], ["09", "Languages", "Hasta 9 idiomas por app", "sky"], ["100%", "Local-first", "Registros centrados en el dispositivo", "rose"], ["1x", "Team size", "Estudio de una sola persona", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "Aprendizaje de japonés. Primer lanzamiento.", "Shipped", "done"], ["2026", "LunaTarot", "Un diario de tarot con una baraja original de 78 cartas.", "Shipped", "done"], ["2026", "Add Up", "Tareas, rutinas y hojas de ruta. Ya en el App Store.", "Shipped", "done"], ["2026", "Stirred", "Una guía interactiva de cócteles. Ya en el App Store y Google Play.", "Shipped", "done"], ["2026", "DailySign", "Lengua de señas coreana, cinco palabras al día. Muy pronto.", "Coming soon", "now"], ["Later", "Cosas más pequeñas", "Seguiremos creando herramientas tranquilas y amables.", "Soon", "next"]],
    global: { title: ["En cualquier parte del mundo,", "con el mismo tono."], desc: "Las apps de Daydream Factory se lanzan en hasta nueve idiomas, incluidos coreano, inglés y japonés. Intentamos conservar la misma sensibilidad en cada traducción." },
    contact: { title: ["¿Tienes", "una pequeña app en mente?"], cards: [{ label: "Email", value: "support@daydreamfactory.net", cta: "Enviar correo", accent: "sun" }, { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "Sobre el estudio", accent: "rose" }, { label: "Collab", value: "Abiertos a ideas y colaboraciones", cta: "Hablemos", accent: "lav" }] },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
  FR: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody: "Daydream Factory est un studio logiciel d'une seule personne basé à Séoul. Il crée de petites applications pour la productivité, l'apprentissage des langues, le journal personnel et les loisirs, sur iOS et Android.",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["Voir les apps", "À propos du studio"],
    heroTiles: {
      addup: "Tâches · routines · feuilles de route",
      kana: "Le japonais par les exemples",
      luna: "Journal émotionnel avec tarot",
      stirred: "Guide interactif de cocktails",
      sueo: "Langue des signes coréenne, au quotidien",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["De grands objectifs, une étape à la fois", "Dix minutes de japonais par jour", "Jeu de tarot original de 78 cartes", "Soixante-dix cocktails à préparer soi-même", "La langue des signes coréenne, cinq mots par jour", "Jusqu'à 9 langues par app", "Un studio solo, lentement", "Les notes restent proches de vous"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "Voir les détails",
      readOnline: "Lire sur le web",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "Langue actuelle",
      moreComing: "More coming",
      studioNotes: "Notre manière de travailler",
      byTheNumbers: "Petit studio, chiffres nets",
      roadmap: "Ce qui est fait, ce qui vient",
      global: "One studio, many languages",
      contact: "Contact",
      promises: "Trois promesses.",
    },
    addup: {
      title: "Add Up,",
      subtitle: "de grands objectifs, une étape débloquée à la fois.",
      desc: "Une app de tâches et de routines où noter une idée prend trois secondes, et où les grands objectifs deviennent des feuilles de route qui se débloquent étape par étape. Chaque tâche cochée s'accompagne d'une petite célébration, et la timeline hebdomadaire montre votre journée comme un flux. Sans compte — synchronisée avec iCloud.",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["Saisie en 3 secondes", "La barre d'entrée est toujours là"], ["Feuilles de route", "Débloquez vos objectifs étape par étape"], ["Célébrations", "Petites fêtes et badges de progression"], ["Timeline", "Glissez les cartes pour réorganiser la journée"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "le japonais par les exemples.",
      desc: "KanaTomo est une application de vocabulaire japonais construite autour des mots JLPT, des exemples, de l'audio et des révisions. Les mots restent grâce au contexte, pas seulement à la mémorisation.",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "Vocabulaire officiel"], ["Apprentissage contextuel", "Conçu autour d'exemples"], ["Audio TTS", "Prononciation naturelle"], ["Enregistrement local", "Historique sur l'appareil"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "une petite app de lecture nocturne.",
      desc: "Avec le jeu original Mystic Vintage Gold Tarot de 78 cartes, LunaTarot est une application calme pour noter émotions et questions. Les historiques restent sur l'appareil et l'analyse IA n'apparaît qu'au bon moment.",
      sub: "iOS · Android · 7 languages",
      features: [["Jeu de 78 cartes", "Mystic Vintage Gold"], ["Plusieurs tirages", "Carte du jour · tirage à 3 cartes"], ["Lecture IA", "Interprétation plus longue si besoin"], ["Enregistrement local", "Historique sur l'appareil"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "ne lisez plus les recettes — préparez le cocktail.",
      desc: "Un guide interactif de cocktails pour découvrir des recettes, les préparer étape par étape et bâtir son propre bar. Parcourez une encyclopédie de 70 cocktails avec recherche et filtres, puis réalisez chaque verre en mode pratique — dans une ambiance sombre de bar.",
      sub: "iOS · Android · 3 languages",
      features: [["70 cocktails", "Des classiques aux spécialités locales"], ["Mode pratique", "Chaque verre, étape par étape"], ["Mon bar", "Des idées selon vos bouteilles"], ["Mes recettes", "Composez et enregistrez les vôtres"]],
    },
    sueo: {
      name: "DailySign",
      title: "DailySign,",
      subtitle: "cinq mots de langue des signes coréenne par jour.",
      desc: "Une app pour apprendre la langue des signes coréenne (KSL) avec de vraies vidéos du dictionnaire de l'Institut national de la langue coréenne. Un parcours en 20 étapes dans l'ordre de la vie quotidienne, cinq mots par jour avec un entraînement en miroir. App en coréen.",
      sub: "iOS · Android · Coréen",
      features: [["5 par jour", "Une routine légère avec séries"], ["Vraies vidéos", "300 vidéos du dictionnaire"], ["Entraînement miroir", "Comparez avec votre caméra"], ["20 étapes", "De la famille au quotidien"]],
    },
    web: {
      note: "Dans le navigateur, sans installation",
      visit: "Visiter",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "Un service web gratuit d'horoscopes, d'interprétation des rêves et d'un dictionnaire de tarot de 78 cartes — sans compte.", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "Boys & Girls", tag: "Tests psychologiques de 3 minutes", desc: "Des tests de six questions sur l'amour, le travail, les dépenses et le sommeil. Sans inscription ni stockage : les résultats sont calculés dans votre navigateur. Service en coréen.", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "Nous construisons lentement.", body: "Nous avançons application par application. Nous privilégions la finesse et la durée à la vitesse.", accent: "lav" },
      { number: "02", label: "Quietly", title: "Des interfaces calmes.", body: "Nous n'insistons pas avec du bruit. Ce sont des outils que l'on ouvre seulement quand on en a besoin.", accent: "sky" },
      { number: "03", label: "Privately", title: "Les notes restent proches.", body: "Nous gardons les données personnelles aussi près que possible de l'appareil et nous expliquons clairement les traitements externes.", accent: "rose" },
    ],
    stats: [["05", "Apps", "4 publiées · 1 bientôt", "blue"], ["09", "Languages", "Jusqu'à 9 langues par app", "sky"], ["100%", "Local-first", "Historique centré sur l'appareil", "rose"], ["1x", "Team size", "Studio solo", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "Apprentissage du japonais. Premier lancement.", "Shipped", "done"], ["2026", "LunaTarot", "Journal de tarot avec un jeu original de 78 cartes.", "Shipped", "done"], ["2026", "Add Up", "Tâches, routines et feuilles de route. Disponible sur l'App Store.", "Shipped", "done"], ["2026", "Stirred", "Un guide interactif de cocktails. Disponible sur l'App Store et Google Play.", "Shipped", "done"], ["2026", "DailySign", "La langue des signes coréenne, cinq mots par jour. Bientôt.", "Coming soon", "now"], ["Later", "Encore plus petit", "D'autres outils calmes et doux suivront.", "Soon", "next"]],
    global: { title: ["Partout dans le monde,", "avec le même ton."], desc: "Les apps de Daydream Factory sont disponibles en neuf langues au maximum, dont le coréen, l'anglais et le japonais. Nous essayons de garder la même sensibilité dans chaque version." },
    contact: { title: ["Vous avez", "une petite app à créer ?"], cards: [{ label: "Email", value: "support@daydreamfactory.net", cta: "Envoyer un mail", accent: "sun" }, { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "Le studio", accent: "rose" }, { label: "Collab", value: "Ouvert aux idées et collaborations", cta: "Discutons", accent: "lav" }] },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
  PT: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody: "Daydream Factory é um estúdio de software de uma só pessoa em Seul. Cria pequenos apps de produtividade, aprendizado de idiomas, diários e hobbies para iOS e Android.",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["Ver apps", "Sobre o estúdio"],
    heroTiles: {
      addup: "Tarefas · rotinas · roteiros de metas",
      kana: "Japonês por frases de exemplo",
      luna: "Diário emocional com tarô",
      stirred: "Guia interativo de coquetéis",
      sueo: "Língua de sinais coreana, diariamente",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["Metas grandes, um passo de cada vez", "Dez minutos de japonês por dia", "Baralho original de 78 cartas", "Setenta coquetéis que você mesmo prepara", "Língua de sinais coreana, cinco palavras por dia", "Até 9 idiomas por app", "Um estúdio solo, devagar", "Seus registros ficam por perto"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "Ver detalhes",
      readOnline: "Ler na web",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "Idioma atual",
      moreComing: "More coming",
      studioNotes: "Como trabalhamos",
      byTheNumbers: "Estúdio pequeno, números claros",
      roadmap: "O que fizemos e o que vem",
      global: "One studio, many languages",
      contact: "Contato",
      promises: "Três promessas.",
    },
    addup: {
      title: "Add Up,",
      subtitle: "metas grandes, um passo desbloqueado de cada vez.",
      desc: "Um app de tarefas e rotinas em que anotar algo leva três segundos, e metas grandes viram roteiros que se desbloqueiam etapa por etapa. Cada tarefa concluída vem com uma pequena celebração, e a linha do tempo semanal mostra o dia como um fluxo. Sem login — sincronizado com iCloud.",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["Captura em 3 segundos", "A barra de entrada está sempre ali"], ["Roteiros", "Desbloqueie metas passo a passo"], ["Celebrações", "Pequenas comemorações e medalhas"], ["Linha do tempo", "Arraste cartões para mover o dia"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "japonês por frases de exemplo.",
      desc: "KanaTomo é um app de vocabulário japonês baseado em palavras do JLPT, frases de exemplo, áudio e revisão. Ele foi pensado para que as palavras permaneçam pelo contexto, não só pela memorização.",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "Vocabulário oficial"], ["Aprendizado contextual", "Design baseado em exemplos"], ["Áudio TTS", "Pronúncia natural"], ["Registro local", "Histórico salvo no aparelho"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "um pequeno app de leitura noturna.",
      desc: "Com o baralho original Mystic Vintage Gold Tarot de 78 cartas, LunaTarot é um app silencioso para registrar perguntas e emoções. O histórico fica no aparelho e a análise por IA aparece apenas quando necessária.",
      sub: "iOS · Android · 7 languages",
      features: [["Baralho de 78 cartas", "Mystic Vintage Gold"], ["Vários spreads", "Carta do dia · 3 cartas"], ["Leitura com IA", "Interpretação mais longa quando preciso"], ["Registro local", "Histórico salvo no aparelho"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "não só leia receitas — prepare o coquetel.",
      desc: "Um guia interativo de coquetéis para descobrir receitas, prepará-las passo a passo e montar o seu próprio bar. Explore uma enciclopédia de 70 coquetéis com busca e filtros e complete cada drinque no modo prática — tudo em um clima escuro de bar.",
      sub: "iOS · Android · 3 languages",
      features: [["70 coquetéis", "Dos clássicos aos regionais"], ["Modo prática", "Cada drinque passo a passo"], ["Meu bar", "Drinques com o que você tem em casa"], ["Minhas receitas", "Crie e salve as suas"]],
    },
    sueo: {
      name: "DailySign",
      title: "DailySign,",
      subtitle: "cinco palavras de língua de sinais coreana por dia.",
      desc: "Um app para aprender a língua de sinais coreana (KSL) com vídeos reais do dicionário do Instituto Nacional da Língua Coreana. Um currículo de 20 etapas na ordem da vida diária, cinco palavras por dia com prática no espelho. App em coreano.",
      sub: "iOS · Android · Coreano",
      features: [["5 por dia", "Rotina leve com sequências"], ["Vídeos reais", "300 vídeos do dicionário"], ["Prática no espelho", "Compare com sua câmera"], ["20 etapas", "Da família ao dia a dia"]],
    },
    web: {
      note: "No navegador, sem instalar",
      visit: "Visitar",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "Um serviço web gratuito de horóscopos, interpretação de sonhos e um dicionário de tarô de 78 cartas — sem conta.", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "Boys & Girls", tag: "Testes de personalidade de 3 minutos", desc: "Testes de seis perguntas sobre amor, trabalho, gastos e sono. Sem cadastro nem armazenamento: os resultados são calculados no seu navegador. Serviço em coreano.", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "Construímos devagar.", body: "Focamos em um app de cada vez. Preferimos refinamento e duração à velocidade.", accent: "lav" },
      { number: "02", label: "Quietly", title: "Interfaces silenciosas.", body: "Não pressionamos as pessoas com ruído. Queremos ser ferramentas abertas apenas quando realmente são necessárias.", accent: "sky" },
      { number: "03", label: "Privately", title: "Os registros ficam por perto.", body: "Mantemos os dados pessoais o mais perto possível do aparelho e explicamos claramente qualquer processamento externo.", accent: "rose" },
    ],
    stats: [["05", "Apps", "4 lançados · 1 em breve", "blue"], ["09", "Languages", "Até 9 idiomas por app", "sky"], ["100%", "Local-first", "Registros centrados no aparelho", "rose"], ["1x", "Team size", "Estúdio solo", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "Aprendizado de japonês. Primeiro lançamento.", "Shipped", "done"], ["2026", "LunaTarot", "Um diário de tarot com um baralho original de 78 cartas.", "Shipped", "done"], ["2026", "Add Up", "Tarefas, rotinas e roteiros de metas. Já na App Store.", "Shipped", "done"], ["2026", "Stirred", "Um guia interativo de coquetéis. Já na App Store e no Google Play.", "Shipped", "done"], ["2026", "DailySign", "Língua de sinais coreana, cinco palavras por dia. Em breve.", "Coming soon", "now"], ["Later", "Coisas ainda menores", "Mais ferramentas calmas e gentis virão.", "Soon", "next"]],
    global: { title: ["Em qualquer lugar do mundo,", "com o mesmo tom."], desc: "Os apps da Daydream Factory são lançados em até nove idiomas, incluindo coreano, inglês e japonês. Tentamos manter a mesma sensibilidade em todas as versões." },
    contact: { title: ["Você tem", "um pequeno app para criar?"], cards: [{ label: "Email", value: "support@daydreamfactory.net", cta: "Enviar email", accent: "sun" }, { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "Sobre o estúdio", accent: "rose" }, { label: "Collab", value: "Aberto a ideias e colaborações", cta: "Vamos conversar", accent: "lav" }] },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
  VI: {
    nav: { about: "About", numbers: "Numbers", contact: "Contact" },
    eyebrow: "A one-person software studio",
    heroTitle: ["Small apps,", "made with care."],
    heroBody: "Daydream Factory là một studio phần mềm một người tại Seoul, tạo ra những ứng dụng nhỏ cho năng suất, học ngôn ngữ, ghi chép và sở thích trên iOS và Android.",
    heroStats: ["Apps", "Languages", "Since"],
    heroCtas: ["Xem ứng dụng", "Về studio"],
    heroTiles: {
      addup: "Việc cần làm · thói quen · lộ trình mục tiêu",
      kana: "Học tiếng Nhật qua câu ví dụ",
      luna: "Nhật ký cảm xúc với tarot",
      stirred: "Cẩm nang cocktail tương tác",
      sueo: "Ngôn ngữ ký hiệu Hàn Quốc mỗi ngày",
      newBadge: "New",
      shippedBadge: "Shipped",
      soonBadge: "Coming soon",
    },
    marquee: ["Mục tiêu lớn, từng bước một", "Mười phút tiếng Nhật mỗi ngày", "Bộ tarot gốc 78 lá", "70 ly cocktail tự tay pha chế", "Ngôn ngữ ký hiệu Hàn Quốc, năm từ mỗi ngày", "Tối đa 9 ngôn ngữ mỗi ứng dụng", "Studio một người, chậm rãi", "Ghi chép luôn ở gần bạn"],
    common: {
      featuredApp: "Featured app",
      viewDetails: "Xem chi tiết",
      readOnline: "Đọc trên web",
      appStore: "App Store",
      googlePlay: "Google Play",
      currentLocale: "Ngôn ngữ hiện tại",
      moreComing: "More coming",
      studioNotes: "Cách chúng tôi làm việc",
      byTheNumbers: "Studio nhỏ, con số rõ ràng",
      roadmap: "Đã làm gì, sắp làm gì",
      global: "One studio, many languages",
      contact: "Liên hệ",
      promises: "Ba lời hứa.",
    },
    addup: {
      title: "Add Up,",
      subtitle: "mục tiêu lớn, mở khóa từng bước một.",
      desc: "Ứng dụng việc cần làm và thói quen, nơi ghi lại một việc chỉ mất ba giây, còn mục tiêu lớn được chia thành lộ trình mở khóa từng giai đoạn. Mỗi lần hoàn thành đều có lời chúc mừng nhỏ, và dòng thời gian tuần cho thấy một ngày như một dòng chảy. Không cần đăng nhập — đồng bộ qua iCloud.",
      sub: "iOS · Apple Watch · 9 languages",
      features: [["Ghi trong 3 giây", "Thanh nhập luôn sẵn sàng"], ["Lộ trình", "Mở khóa mục tiêu từng bước"], ["Chúc mừng", "Niềm vui nhỏ và huy hiệu trưởng thành"], ["Dòng thời gian", "Kéo thẻ để sắp xếp lại ngày"]],
    },
    kana: {
      title: "KanaTomo,",
      subtitle: "học tiếng Nhật qua câu ví dụ.",
      desc: "KanaTomo là ứng dụng từ vựng tiếng Nhật xoay quanh từ JLPT, câu ví dụ, âm thanh và ôn tập. Ứng dụng được thiết kế để từ ngữ ở lại nhờ ngữ cảnh, không chỉ bằng ghi nhớ.",
      sub: "iOS · Android · 7 languages",
      features: [["JLPT N1-N5", "Từ vựng chính thức"], ["Học theo ngữ cảnh", "Thiết kế dựa trên ví dụ"], ["Âm thanh TTS", "Phát âm tự nhiên"], ["Lưu cục bộ", "Lịch sử nằm trong thiết bị"]],
    },
    luna: {
      title: "LunaTarot,",
      subtitle: "một ứng dụng trải bài nhỏ cho đêm.",
      desc: "Với bộ bài gốc Mystic Vintage Gold Tarot gồm 78 lá, LunaTarot là ứng dụng nhật ký tarot yên tĩnh để ghi lại câu hỏi và cảm xúc. Lịch sử nằm trong thiết bị, còn phân tích AI chỉ xuất hiện khi cần.",
      sub: "iOS · Android · 7 languages",
      features: [["Bộ 78 lá", "Mystic Vintage Gold"], ["Nhiều trải bài", "Lá bài hôm nay · trải 3 lá"], ["Diễn giải AI", "Phân tích dài hơn khi cần"], ["Lưu cục bộ", "Lịch sử nằm trong thiết bị"]],
    },
    stirred: {
      title: "Stirred,",
      subtitle: "đừng chỉ đọc công thức — hãy pha ly cocktail.",
      desc: "Cẩm nang cocktail tương tác để khám phá công thức, pha từng bước và xây dựng quầy bar của riêng bạn. Duyệt bộ sưu tập 70 loại cocktail với tìm kiếm và bộ lọc, rồi hoàn thành từng ly trong chế độ luyện tập — trong không gian tối như một quán bar.",
      sub: "iOS · Android · 3 languages",
      features: [["70 loại cocktail", "Từ cổ điển đến đặc trưng vùng miền"], ["Chế độ luyện tập", "Pha từng ly theo từng bước"], ["Quầy bar của tôi", "Gợi ý từ nguyên liệu sẵn có"], ["Công thức của tôi", "Tự tạo và lưu công thức"]],
    },
    sueo: {
      name: "DailySign",
      title: "DailySign,",
      subtitle: "năm từ ngôn ngữ ký hiệu Hàn Quốc mỗi ngày.",
      desc: "Ứng dụng học ngôn ngữ ký hiệu Hàn Quốc (KSL) với video thật từ từ điển của Viện Ngôn ngữ Quốc gia Hàn Quốc. Giáo trình 20 giai đoạn theo trình tự cuộc sống, mỗi ngày năm từ cùng chế độ luyện tập qua gương. Ứng dụng tiếng Hàn.",
      sub: "iOS · Android · Tiếng Hàn",
      features: [["5 từ mỗi ngày", "Thói quen nhẹ nhàng với chuỗi ngày"], ["Video thật", "300 video từ từ điển"], ["Luyện qua gương", "So sánh với camera của bạn"], ["Giáo trình 20 bước", "Từ gia đình đến đời sống"]],
    },
    web: {
      note: "Ngay trên trình duyệt, không cần cài đặt",
      visit: "Truy cập",
      cards: [
        { name: "Luna", tag: "Horoscopes · Dreams · Tarot", desc: "Dịch vụ web miễn phí về tử vi, giải mã giấc mơ và từ điển tarot 78 lá — không cần tài khoản.", langs: "KO · EN · JA", url: "https://luna.daydreamfactory.net", accent: "lav" },
        { name: "Boys & Girls", tag: "Trắc nghiệm tâm lý 3 phút", desc: "Bộ trắc nghiệm 6 câu về tình yêu, công việc, chi tiêu và giấc ngủ. Không đăng ký, không lưu trữ — kết quả được tính ngay trên trình duyệt. Dịch vụ tiếng Hàn.", langs: "KO", url: "https://boysngirls.co.kr", accent: "rose" },
      ],
    },
    principles: [
      { number: "01", label: "Slowly", title: "Chúng tôi làm chậm rãi.", body: "Chúng tôi tập trung vào từng ứng dụng một. Chúng tôi chọn độ hoàn thiện và sự bền lâu thay vì tốc độ.", accent: "lav" },
      { number: "02", label: "Quietly", title: "Giao diện yên tĩnh.", body: "Chúng tôi không thúc ép người dùng bằng sự ồn ào. Đây là những công cụ chỉ mở ra khi thực sự cần.", accent: "sky" },
      { number: "03", label: "Privately", title: "Ghi chép ở gần người dùng.", body: "Chúng tôi giữ dữ liệu cá nhân gần thiết bị nhất có thể và giải thích rõ mọi xử lý bên ngoài.", accent: "rose" },
    ],
    stats: [["05", "Apps", "4 đã phát hành · 1 sắp ra mắt", "blue"], ["09", "Languages", "Tối đa 9 ngôn ngữ mỗi ứng dụng", "sky"], ["100%", "Local-first", "Lịch sử tập trung trên thiết bị", "rose"], ["1x", "Team size", "Studio một người", "sun"]],
    roadmap: [["2025.12", "KanaTomo", "Học tiếng Nhật. Bản phát hành đầu tiên.", "Shipped", "done"], ["2026", "LunaTarot", "Nhật ký tarot với bộ bài gốc 78 lá.", "Shipped", "done"], ["2026", "Add Up", "Việc cần làm, thói quen và lộ trình mục tiêu. Đã có trên App Store.", "Shipped", "done"], ["2026", "Stirred", "Cẩm nang cocktail tương tác. Đã có trên App Store và Google Play.", "Shipped", "done"], ["2026", "DailySign", "Ngôn ngữ ký hiệu Hàn Quốc, năm từ mỗi ngày. Sắp ra mắt.", "Coming soon", "now"], ["Later", "Những thứ nhỏ hơn nữa", "Sẽ còn thêm những công cụ nhẹ nhàng và yên tĩnh.", "Soon", "next"]],
    global: { title: ["Ở bất cứ đâu trên thế giới,", "vẫn cùng một tông."], desc: "Các ứng dụng của Daydream Factory được phát hành bằng tối đa chín ngôn ngữ, bao gồm tiếng Hàn, tiếng Anh và tiếng Nhật. Chúng tôi cố giữ cùng một cảm giác trong mọi bản dịch." },
    contact: { title: ["Bạn có", "một ứng dụng nhỏ muốn làm không?"], cards: [{ label: "Email", value: "support@daydreamfactory.net", cta: "Gửi email", accent: "sun" }, { label: "Studio", value: "Seoul, KR · Apps since Dec 2025", cta: "Về studio", accent: "rose" }, { label: "Collab", value: "Chào đón ý tưởng và hợp tác", cta: "Trao đổi", accent: "lav" }] },
    footer: "© 2026 Daydream Factory · All rights reserved.",
  },
};

const App: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("KO");

  const handleStoreClick = React.useCallback(
    (appName: "kanatomo" | "lunatarot" | "addup" | "stirred", storeName: "app_store" | "google_play") => () => {
      trackStoreClick({
        app_name: appName,
        store_name: storeName,
        source_page: "home",
      });
    },
    []
  );

  React.useEffect(() => {
    setLocale(detectLocale());
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale.toLowerCase();
    }
  }, [locale]);

  const copy = baseCopy[locale];
  const addupShots = locale === "KO" ? [addupShotKo1, addupShotKo2] : [addupShotEn1, addupShotEn2];
  const stirredShots =
    locale === "KO"
      ? [stirredShotKo1, stirredShotKo2]
      : locale === "JA"
        ? [stirredShotJa1, stirredShotJa2]
        : [stirredShotEn1, stirredShotEn2];

  return (
    <div className="ddf-page">
      <header className="ddf-topbar">
        <div className="ddf-topbar-inner">
          <a href="#hero" className="ddf-brand">
            <img src={daydreamLogo} alt="Daydream Factory logo" className="ddf-brand-mark" />
            <span>Daydream Factory</span>
          </a>

          <nav className="ddf-nav" aria-label="Primary">
            <a href="#addup">Add Up</a>
            <a href="#kana">KanaTomo</a>
            <a href="#luna">LunaTarot</a>
            <a href="#stirred">Stirred</a>
            <a href="#dailysign">{copy.sueo.name}</a>
            <a href="#studio">{copy.nav.about}</a>
            <a href="#contact">{copy.nav.contact}</a>
          </nav>

          <div className="ddf-locale-pills" aria-label="Locales">
            {locales.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setLocale(item)}
                className={`ddf-locale-pill${locale === item ? " is-active" : ""}`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main>
        <section id="hero" className="ddf-hero">
          <div>
            <div className="ddf-eyebrow">
              <span>✦</span>
              <span>{copy.eyebrow}</span>
            </div>

            <h1 className="ddf-hero-title">
              {copy.heroTitle[0]}
              <br />
              <span className="is-blue">{copy.heroTitle[1]}</span>
            </h1>

            <p className="ddf-hero-body">{copy.heroBody}</p>

            <div className="ddf-hero-actions">
              <a href="#addup" className="ddf-btn ddf-btn-primary">
                {copy.heroCtas[0]}
              </a>
              <a href="#studio" className="ddf-btn ddf-btn-ghost">
                {copy.heroCtas[1]}
              </a>
            </div>

            <div className="ddf-hero-stats">
              <div>
                <strong>05</strong>
                <span>{copy.heroStats[0]}</span>
              </div>
              <div>
                <strong>09</strong>
                <span>{copy.heroStats[1]}</span>
              </div>
              <div>
                <strong>2025.12</strong>
                <span>{copy.heroStats[2]}</span>
              </div>
            </div>
          </div>

          <div className="ddf-hero-panel">
            <Link to="/addup" className="ddf-hero-tile">
              <img src={addupLogo} alt="Add Up icon" className="ddf-hero-tile-icon" />
              <div className="ddf-hero-tile-info">
                <strong>Add Up</strong>
                <span>{copy.heroTiles.addup}</span>
              </div>
              <span className="ddf-hero-tile-chip">{copy.heroTiles.newBadge}</span>
            </Link>

            <Link to="/kanatomo" className="ddf-hero-tile is-kana">
              <img src={kanaLogo} alt="KanaTomo icon" className="ddf-hero-tile-icon" />
              <div className="ddf-hero-tile-info">
                <strong>KanaTomo</strong>
                <span>{copy.heroTiles.kana}</span>
              </div>
              <span className="ddf-hero-tile-chip">{copy.heroTiles.shippedBadge}</span>
            </Link>

            <Link to="/lunatarot" className="ddf-hero-tile is-luna">
              <img src={lunaLogo} alt="LunaTarot icon" className="ddf-hero-tile-icon" />
              <div className="ddf-hero-tile-info">
                <strong>LunaTarot</strong>
                <span>{copy.heroTiles.luna}</span>
              </div>
              <span className="ddf-hero-tile-chip">{copy.heroTiles.shippedBadge}</span>
            </Link>

            <Link to="/stirred" className="ddf-hero-tile is-stirred">
              <img src={stirredLogo} alt="Stirred icon" className="ddf-hero-tile-icon" />
              <div className="ddf-hero-tile-info">
                <strong>Stirred</strong>
                <span>{copy.heroTiles.stirred}</span>
              </div>
              <span className="ddf-hero-tile-chip">{copy.heroTiles.newBadge}</span>
            </Link>

            <Link to="/dailysign" className="ddf-hero-tile is-sueo">
              <img src={sueoLogo} alt={`${copy.sueo.name} icon`} className="ddf-hero-tile-icon" />
              <div className="ddf-hero-tile-info">
                <strong>{copy.sueo.name}</strong>
                <span>{copy.heroTiles.sueo}</span>
              </div>
              <span className="ddf-hero-tile-chip">{copy.heroTiles.soonBadge}</span>
            </Link>
          </div>
        </section>

        <section className="ddf-marquee" aria-label="Highlights">
          <div className="ddf-marquee-track">
            {[...copy.marquee, ...copy.marquee].map((item, index) => (
              <span key={`${item}-${index}`}>
                {item}
                <em>✦</em>
              </span>
            ))}
          </div>
        </section>

        <section id="addup" className="ddf-section ddf-app-section">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot" />
                <span>App · 01 — {copy.common.featuredApp}</span>
              </div>
              <span className="ddf-section-note">Add Up</span>
            </div>

            <div className="ddf-app-grid">
              <div className="ddf-app-copy">
                <div className="ddf-app-id">
                  <img src={addupLogo} alt="Add Up icon" className="ddf-app-icon" />
                  <div>
                    <p className="ddf-app-tag">Productivity · To-do</p>
                    <p className="ddf-app-sub">{copy.addup.sub}</p>
                  </div>
                </div>

                <h2 className="ddf-app-title">
                  {copy.addup.title}
                  <br />
                  <span className="is-accent">{copy.addup.subtitle}</span>
                </h2>

                <p className="ddf-app-desc">{copy.addup.desc}</p>

                <div className="ddf-feature-grid">
                  {copy.addup.features.map(([title, sub]) => (
                    <article key={title} className="ddf-feature-card">
                      <h3>{title}</h3>
                      <p>{sub}</p>
                    </article>
                  ))}
                </div>

                <div className="ddf-app-actions">
                  <a className="ddf-store-btn" href="https://apps.apple.com/app/id6776875043" target="_blank" rel="noreferrer" onClick={handleStoreClick("addup", "app_store")}>
                    <span className="ddf-store-dot" />
                    {copy.common.appStore}
                  </a>
                  <Link to="/addup" className="ddf-text-link">
                    {copy.common.viewDetails}
                  </Link>
                </div>
              </div>

              <div className="ddf-shots">
                <img src={addupShots[0]} alt="Add Up screenshot 1" className="ddf-shot" />
                <img src={addupShots[1]} alt="Add Up screenshot 2" className="ddf-shot" />
              </div>
            </div>
          </div>
        </section>

        <section id="kana" className="ddf-section ddf-app-section is-kana is-soft">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-kana" />
                <span>App · 02 — {copy.common.featuredApp}</span>
              </div>
              <span className="ddf-section-note">KanaTomo</span>
            </div>

            <div className="ddf-app-grid is-reversed">
              <div className="ddf-shots">
                <img src={kanaShot1} alt="KanaTomo screenshot 1" className="ddf-shot" />
                <img src={kanaShot2} alt="KanaTomo screenshot 2" className="ddf-shot" />
              </div>

              <div className="ddf-app-copy">
                <div className="ddf-app-id">
                  <img src={kanaLogo} alt="KanaTomo icon" className="ddf-app-icon" />
                  <div>
                    <p className="ddf-app-tag">Japanese · Learning</p>
                    <p className="ddf-app-sub">{copy.kana.sub}</p>
                  </div>
                </div>

                <h2 className="ddf-app-title">
                  {copy.kana.title}
                  <br />
                  <span className="is-accent">{copy.kana.subtitle}</span>
                </h2>

                <p className="ddf-app-desc">{copy.kana.desc}</p>

                <div className="ddf-feature-grid">
                  {copy.kana.features.map(([title, sub]) => (
                    <article key={title} className="ddf-feature-card">
                      <h3>{title}</h3>
                      <p>{sub}</p>
                    </article>
                  ))}
                </div>

                <div className="ddf-app-actions">
                  <a className="ddf-store-btn" href="https://apps.apple.com/kr/app/kanatomo/id6749524613" target="_blank" rel="noreferrer" onClick={handleStoreClick("kanatomo", "app_store")}>
                    <span className="ddf-store-dot" />
                    {copy.common.appStore}
                  </a>
                  <a className="ddf-store-btn" href="https://play.google.com/store/apps/details?id=net.daydreamfactory.kanatomo" target="_blank" rel="noreferrer" onClick={handleStoreClick("kanatomo", "google_play")}>
                    <span className="ddf-store-dot" />
                    {copy.common.googlePlay}
                  </a>
                  <Link to="/kanatomo" className="ddf-text-link">
                    {copy.common.viewDetails}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="luna" className="ddf-section ddf-app-section is-luna">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-luna" />
                <span>App · 03 — {copy.common.featuredApp}</span>
              </div>
              <span className="ddf-section-note">LunaTarot</span>
            </div>

            <div className="ddf-app-grid">
              <div className="ddf-app-copy">
                <div className="ddf-app-id">
                  <img src={lunaLogo} alt="LunaTarot icon" className="ddf-app-icon" />
                  <div>
                    <p className="ddf-app-tag">Journaling · Tarot</p>
                    <p className="ddf-app-sub">{copy.luna.sub}</p>
                  </div>
                </div>

                <h2 className="ddf-app-title">
                  {copy.luna.title}
                  <br />
                  <span className="is-accent">{copy.luna.subtitle}</span>
                </h2>

                <p className="ddf-app-desc">{copy.luna.desc}</p>

                <div className="ddf-feature-grid">
                  {copy.luna.features.map(([title, sub]) => (
                    <article key={title} className="ddf-feature-card">
                      <h3>{title}</h3>
                      <p>{sub}</p>
                    </article>
                  ))}
                </div>

                <div className="ddf-app-actions">
                  <a className="ddf-store-btn" href="https://apps.apple.com/app/id6755215945" target="_blank" rel="noreferrer" onClick={handleStoreClick("lunatarot", "app_store")}>
                    <span className="ddf-store-dot" />
                    {copy.common.appStore}
                  </a>
                  <a className="ddf-store-btn" href="https://play.google.com/store/apps/details?id=net.daydreamfactory.lunatarot" target="_blank" rel="noreferrer" onClick={handleStoreClick("lunatarot", "google_play")}>
                    <span className="ddf-store-dot" />
                    {copy.common.googlePlay}
                  </a>
                  <Link to="/lunatarot" className="ddf-text-link">
                    {copy.common.viewDetails}
                  </Link>
                  {/* 웹 콘텐츠 사이트로 나가는 통로.
                      이 도메인의 본문이 어디 있는지 방문자와 크롤러 모두에게 알린다. */}
                  <a
                    className="ddf-text-link"
                    href="https://luna.daydreamfactory.net/en"
                  >
                    {copy.common.readOnline}
                  </a>
                </div>
              </div>

              <div className="ddf-shots">
                <img src={lunaShot1} alt="LunaTarot screenshot 1" className="ddf-shot" />
                <img src={lunaShot2} alt="LunaTarot screenshot 2" className="ddf-shot" />
              </div>
            </div>
          </div>
        </section>

        <section id="stirred" className="ddf-section ddf-app-section is-stirred is-deep">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-stirred" />
                <span>App · 04 — {copy.common.featuredApp}</span>
              </div>
              <span className="ddf-section-note">Stirred</span>
            </div>

            <div className="ddf-app-grid is-reversed">
              <div className="ddf-shots">
                <img src={stirredShots[0]} alt="Stirred screenshot 1" className="ddf-shot" />
                <img src={stirredShots[1]} alt="Stirred screenshot 2" className="ddf-shot" />
              </div>

              <div className="ddf-app-copy">
                <div className="ddf-app-id">
                  <img src={stirredLogo} alt="Stirred icon" className="ddf-app-icon" />
                  <div>
                    <p className="ddf-app-tag">Cocktails · Learning</p>
                    <p className="ddf-app-sub">{copy.stirred.sub}</p>
                  </div>
                </div>

                <h2 className="ddf-app-title">
                  {copy.stirred.title}
                  <br />
                  <span className="is-accent">{copy.stirred.subtitle}</span>
                </h2>

                <p className="ddf-app-desc">{copy.stirred.desc}</p>

                <div className="ddf-feature-grid">
                  {copy.stirred.features.map(([title, sub]) => (
                    <article key={title} className="ddf-feature-card">
                      <h3>{title}</h3>
                      <p>{sub}</p>
                    </article>
                  ))}
                </div>

                <div className="ddf-app-actions">
                  <a className="ddf-store-btn" href="https://apps.apple.com/app/id6792024242" target="_blank" rel="noreferrer" onClick={handleStoreClick("stirred", "app_store")}>
                    <span className="ddf-store-dot" />
                    {copy.common.appStore}
                  </a>
                  <a className="ddf-store-btn" href="https://play.google.com/store/apps/details?id=net.daydreamfactory.stirred" target="_blank" rel="noreferrer" onClick={handleStoreClick("stirred", "google_play")}>
                    <span className="ddf-store-dot" />
                    {copy.common.googlePlay}
                  </a>
                  <Link to="/stirred" className="ddf-text-link">
                    {copy.common.viewDetails}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="dailysign" className="ddf-section ddf-app-section is-sueo is-soft">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-sueo" />
                <span>App · 05 — {copy.common.featuredApp}</span>
              </div>
              <span className="ddf-section-note">{copy.sueo.name}</span>
            </div>

            <div className="ddf-app-grid">
              <div className="ddf-app-copy">
                <div className="ddf-app-id">
                  <img src={sueoLogo} alt={`${copy.sueo.name} icon`} className="ddf-app-icon" />
                  <div>
                    <p className="ddf-app-tag">Sign Language · Education</p>
                    <p className="ddf-app-sub">{copy.sueo.sub}</p>
                  </div>
                </div>

                <h2 className="ddf-app-title">
                  {copy.sueo.title}
                  <br />
                  <span className="is-accent">{copy.sueo.subtitle}</span>
                </h2>

                <p className="ddf-app-desc">{copy.sueo.desc}</p>

                <div className="ddf-feature-grid">
                  {copy.sueo.features.map(([title, sub]) => (
                    <article key={title} className="ddf-feature-card">
                      <h3>{title}</h3>
                      <p>{sub}</p>
                    </article>
                  ))}
                </div>

                <div className="ddf-app-actions">
                  <span className="ddf-badge-soon">✦ {copy.heroTiles.soonBadge}</span>
                  <Link to="/dailysign" className="ddf-text-link">
                    {copy.common.viewDetails}
                  </Link>
                </div>
              </div>

              <div className="ddf-shots">
                <img src={sueoShot1} alt={`${copy.sueo.name} screenshot 1`} className="ddf-shot" />
                <img src={sueoShot2} alt={`${copy.sueo.name} screenshot 2`} className="ddf-shot" />
              </div>
            </div>
          </div>
        </section>

        <section id="web" className="ddf-section">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot" />
                <span>06 — On the web</span>
              </div>
              <span className="ddf-section-note">{copy.web.note}</span>
            </div>

            <div className="ddf-web-grid">
              {copy.web.cards.map((card) => (
                <a key={card.name} className={`ddf-web-card is-${card.accent}`} href={card.url} target="_blank" rel="noreferrer">
                  <p className="ddf-web-tag">{card.tag}</p>
                  <h3 className="ddf-web-name">{card.name}</h3>
                  <p className="ddf-web-desc">{card.desc}</p>
                  <div className="ddf-web-meta">
                    <span className="ddf-web-langs">{card.langs}</span>
                    <span className="ddf-web-visit">{copy.web.visit} ↗</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="studio" className="ddf-section is-deep">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-sun" />
                <span>07 — Studio notes</span>
              </div>
              <span className="ddf-section-note">{copy.common.studioNotes}</span>
            </div>

            <h2 className="ddf-manifesto-title">
              {copy.common.studioNotes}
              <br />
              <span>{copy.common.promises}</span>
            </h2>

            <div className="ddf-principles">
              {copy.principles.map((item) => (
                <article key={item.number} className={`ddf-principle-card is-${item.accent}`}>
                  <div className="ddf-principle-meta">
                    <span>{item.number}</span>
                    <em>{item.label}</em>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="numbers" className="ddf-section is-soft">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot" />
                <span>08 — By the numbers</span>
              </div>
              <span className="ddf-section-note">{copy.common.byTheNumbers}</span>
            </div>

            <div className="ddf-stats-grid">
              {copy.stats.map(([value, label, sub, accent]) => (
                <article key={label} className="ddf-stat-card">
                  <div className="ddf-stat-value">
                    <span className={`ddf-stat-dot is-${accent}`} />
                    {value}
                  </div>
                  <h3>{label}</h3>
                  <p>{sub}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="roadmap" className="ddf-section">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-sun" />
                <span>09 — Roadmap</span>
              </div>
              <span className="ddf-section-note">{copy.common.roadmap}</span>
            </div>

            <div className="ddf-roadmap-grid">
              {copy.roadmap.map(([year, title, body, badge, state]) => (
                <article key={title} className={`ddf-roadmap-card is-${state}`}>
                  <span className="ddf-roadmap-year">{year}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                  <span className={`ddf-roadmap-badge is-${state}`}>{badge}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="global" className="ddf-section is-soft">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-luna" />
                <span>10 — Global</span>
              </div>
              <span className="ddf-section-note">{copy.common.global}</span>
            </div>

            <div className="ddf-global-grid">
              <div>
                <h2 className="ddf-global-title">
                  {copy.global.title[0]}
                  <br />
                  <span>{copy.global.title[1]}</span>
                </h2>
                <p className="ddf-global-desc">{copy.global.desc}</p>
                <p className="ddf-global-current">
                  {copy.common.currentLocale}: <strong>{localeNames[locale]}</strong>
                </p>
              </div>

              <div className="ddf-language-grid">
                {locales.map((code) => (
                  <article key={code} className={`ddf-language-card${locale === code ? " is-active" : ""}`} onClick={() => setLocale(code)}>
                    <strong>{localeNames[code]}</strong>
                    <span>{code}</span>
                  </article>
                ))}
                <article className="ddf-language-card is-more">
                  <strong>{copy.common.moreComing}</strong>
                  <span>+</span>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="ddf-section is-deep">
          <div className="ddf-section-inner">
            <div className="ddf-section-head">
              <div className="ddf-section-kicker">
                <span className="ddf-kicker-dot is-sun" />
                <span>11 — Contact</span>
              </div>
              <span className="ddf-section-note">{copy.common.contact}</span>
            </div>

            <h2 className="ddf-contact-title">
              {copy.contact.title[0]}
              <br />
              <span>{copy.contact.title[1]}</span>
            </h2>

            <div className="ddf-contact-grid">
              {copy.contact.cards.map((card) => (
                <a
                  key={card.label}
                  className={`ddf-contact-card is-${card.accent}`}
                  href={
                    card.label === "Email"
                      ? "mailto:support@daydreamfactory.net"
                      : card.label === "Collab"
                        ? "mailto:support@daydreamfactory.net?subject=Collab%20inquiry"
                        : "#studio"
                  }
                >
                  <div className="ddf-contact-label">{card.label}</div>
                  <strong>{card.value}</strong>
                  <span>{card.cta}</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="ddf-footer">
        <div className="ddf-footer-inner">
          <div className="ddf-footer-brand">
            <img src={daydreamLogo} alt="Daydream Factory logo" className="ddf-footer-mark" />
            <span>{copy.footer}</span>
          </div>

          <div className="ddf-footer-links">
            <Link to="/addup">Add Up</Link>
            <Link to="/kanatomo">KanaTomo</Link>
            <Link to="/lunatarot">LunaTarot</Link>
            <Link to="/stirred">Stirred</Link>
            <Link to="/dailysign">{copy.sueo.name}</Link>
            <a href="https://luna.daydreamfactory.net" target="_blank" rel="noreferrer">Luna Web</a>
            <a href="https://boysngirls.co.kr" target="_blank" rel="noreferrer">보이즈앤걸즈</a>
            <Link to="/privacy">Privacy</Link>
            <a href="mailto:support@daydreamfactory.net">support@daydreamfactory.net</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
